import { useState, useEffect } from 'react';
import QuestionContainer from './QuestionContainer.jsx';
import {
    CircularProgress,
    Box,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import { executeFetchAsync } from '../../Util.js';
import { config } from '../../Const.js';

const toSafeString = (value) => {
    if (typeof value === 'string') return value;
    if (value == null) return '';
    return String(value);
};

export default function AIQuestionBank({ onAddQuestion, aiQuestionsSubjectText }) {
    const [isLoading, setIsLoading] = useState(false);
    const [questionData, setQuestionData] = useState([]);
    const [numQuestions, setNumQuestions] = useState(10);
    const [subject, setSubject] = useState(toSafeString(aiQuestionsSubjectText));
    const [model, setModel] = useState('CHAT_GPT');

    const handleGenerate = async (e, subjectOverride = null) => {
        if (e) e.preventDefault();

        const effectiveSubject = toSafeString(subjectOverride ?? subject).trim();

        if (!effectiveSubject) return;

        setIsLoading(true);

        const url =
            `${config.SERVER_URL}/api/v1/admin/generateMCQuestions/AI` +
            `?numQuestions=${numQuestions}` +
            `&subject=${encodeURIComponent(effectiveSubject)}` +
            `&model=${model}`;

        try {
            const result = await executeFetchAsync(
                url,
                'get',
                null,
                null,
                null
            );
            setQuestionData(result);
        } catch (err) {
            console.error('Failed to generate questions:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const nextSubject = toSafeString(aiQuestionsSubjectText);
        setSubject(nextSubject);

        if (nextSubject.trim()) {
            handleGenerate(null, nextSubject);
        } else {
            setQuestionData([]);
        }
        // eslint-disable-next-line
    }, [aiQuestionsSubjectText]);

    const qList = questionData.map((q) => (
        <QuestionContainer
            key={q.question.templateId}
            data={q}
            onAdd={onAddQuestion}
            selectedMode={false}
        />
    ));

    return (
        <Box sx={{ height: '100%', pl: '10px', pr: '20px', overflowY: 'auto' }}>
            <Box
                component="form"
                onSubmit={handleGenerate}
                sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}
            >
                <TextField
                    label="Number of questions"
                    type="number"
                    value={numQuestions}
                    onChange={(e) => setNumQuestions(e.target.value)}
                    InputProps={{ inputProps: { min: 1, max: 100 } }}
                    sx={{ width: 140 }}
                    size="small"
                />

                <TextField
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    sx={{ flex: 1 }}
                    size="small"
                />

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="model-select-label">AI Provider</InputLabel>
                    <Select
                        labelId="model-select-label"
                        value={model}
                        label="AI Provider"
                        onChange={(e) => setModel(e.target.value)}
                    >
                        <MenuItem value="CHAT_GPT">ChatGPT</MenuItem>
                        <MenuItem value="GEMINI">Gemini</MenuItem>
                        <MenuItem value="CLAUDE">Claude</MenuItem>
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    type="submit"
                    disabled={isLoading}
                >
                    Generate
                </Button>
            </Box>

            <Box sx={{ mt: '20px' }}>
                {isLoading && (
                    <Box sx={{ padding: '10px' }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && qList}
            </Box>
        </Box>
    );
}